/**
 * Plan covers — the manifest, the per-category pool, and **the only place a cover URL is built.**
 *
 * ── One module, because the source is going to move ──────────────────────────────────────────
 * Covers ship today as bundled files under `public/plan-covers/`. They are going to R2, exactly as
 * the exercise covers did, and the whole point of this module is that the swap is an edit to
 * `coverSource` and to nothing else. No component, no projection and no mapper anywhere else in
 * this app may concatenate a path — if a second one does, the migration becomes a sweep.
 *
 * ── Two rules from the owner, 4 August 2026, and they are product rules ──────────────────────
 * · **Every plan has a cover.** `coverImageKey` is nullable on the wire and the draft carries it as
 *   nullable, but nothing this app AUTHORS may leave it empty and nothing it RENDERS may draw a
 *   blank frame. `planCoverUrl` therefore always answers with a URL: a key it does not recognise,
 *   or no key at all, falls back to the plan's own category pool.
 * · **A cover belongs to its plan's category.** The pool is the keys prefixed `<category>-`, which
 *   is the same filter the mobile app applies. So changing a plan's category has to drag its cover
 *   with it — otherwise mixing comes back through the side door, and a plan switched from Siła to
 *   Cardio keeps a photograph of a barbell.
 *
 * ── The assignment is DETERMINISTIC, never random ────────────────────────────────────────────
 * Five of the six categories currently hold one or two covers; `strength` holds five. A category
 * with more than one needs a choice, and `Math.random()` is the wrong tool for it: the same plan
 * would show a different photograph on every render, and two saves of one draft would store
 * different keys. The seed is the plan's own NAME, folded to an index — so a cover is a stable
 * property of the plan from the moment it is named, and the pool spreads across a trainer's library
 * instead of every new strength plan opening with the same image.
 *
 * The asset gap is real and stated rather than worked around: `bodyweight` and `hybrid` have
 * exactly one cover each, so their "pool" has no choice in it. That is an asset order for the
 * owner, not something this module should paper over by borrowing another category's image — which
 * is precisely what rule two forbids.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Building a string is not DOM
 * access, and keeping the string here is what makes the R2 move one file.
 */

import { PLAN_CATEGORY_ORDER, type PlanCategoryKey } from './planCategory';

/**
 * Which cover a surface is asking for.
 *
 * Two renditions of the same asset, and they are not interchangeable: the row draws a 76×46 crop
 * where a 1600px file is 60× the pixels it can show, and the hero fills 1512px where a 320px
 * thumbnail is a blur. The canon's own note — "kadr 76 × 46 w wierszu i pełnoekranowy w nagłówku to
 * ten sam zasób" — is about identity, not about bytes.
 */
export type PlanCoverVariant = 'hero' | 'thumb';

/**
 * The manifest: every cover key this app may store, grouped by the category it belongs to.
 *
 * **Keys come from here and nowhere else.** The backend does not validate `coverImageKey` — a POST
 * carrying `"totally-not-a-real-key"` is stored verbatim with a 201 — so this list is the whole
 * guarantee. And with no over-the-air updates for the phone, an invented key is a broken frame in
 * the trainer's installed binary until it is rebuilt, not a cosmetic slip.
 *
 * Order matters within a pool: it is the order the picker will draw and the order the deterministic
 * assignment indexes into, so reordering this list re-assigns covers on plans created afterwards.
 * Append rather than insert.
 */
const COVER_POOLS: Record<PlanCategoryKey, readonly string[]> = {
  strength: ['strength-01', 'strength-02', 'strength-03', 'strength-04', 'strength-05'],
  cardio: ['cardio-02', 'cardio-denim'],
  hiit: ['hiit-02', 'hiit-plum'],
  flexibility: ['flexibility-02', 'flexibility-sage'],
  bodyweight: ['bodyweight-02'],
  hybrid: ['hybrid-02'],
};

/**
 * The pool a plan with no recognised category draws from.
 *
 * `hybrid` rather than `strength`, because Hybryda is the neutral-slate category — "mixed" is the
 * honest reading of "this build does not know what this plan is", and it is the one pool whose
 * photograph makes no claim about the training.
 */
const FALLBACK_CATEGORY: PlanCategoryKey = 'hybrid';

/** Every key in the manifest, flattened. The membership test the resolver runs. */
const ALL_COVER_KEYS: readonly string[] = PLAN_CATEGORY_ORDER.flatMap((key) => COVER_POOLS[key]);

/**
 * Where the bytes live. **The one line that changes when covers move to R2.**
 *
 * `/plan-covers/<key>.jpg` and `/plan-covers/<key>.thumb.jpg`, served from `public/`. Root-relative
 * rather than imported as a module asset: the manifest is data, and a bundler `import` per key
 * would put the choice of which cover to show back into whichever module holds the imports.
 */
function coverSource(key: string, variant: PlanCoverVariant): string {
  return variant === 'thumb' ? `/plan-covers/${key}.thumb.jpg` : `/plan-covers/${key}.jpg`;
}

/**
 * A stable, non-negative index for a string.
 *
 * djb2, truncated to 32 bits. Any stable fold would do — what matters is that it is a pure function
 * of the name, so the same plan resolves to the same cover in every session, in both clients, and
 * across a reload. `Math.random()` is what this exists instead of.
 */
function fold(seed: string): number {
  let hash = 5381;
  for (let index = 0; index < seed.length; index += 1) {
    hash = ((hash << 5) + hash + seed.charCodeAt(index)) >>> 0;
  }
  return hash;
}

/**
 * The keys a plan of this category may carry, in the order a picker draws them.
 *
 * The manifest read-only, for the ONE surface that has to draw every option — the builder's cover
 * grid. Everything else asks a question (`coverKeyBelongsTo`, `coverKeyForCategory`) rather than
 * taking the list, because a caller holding the list is a caller that can build a URL, and building
 * a URL is what this module keeps to itself.
 *
 * Two pools hold exactly one key today (`bodyweight`, `hybrid`), so a picker over them offers no
 * choice at all. That is an asset order rather than something to paper over — the picker says so.
 */
export function coverPool(category: PlanCategoryKey | null): readonly string[] {
  return COVER_POOLS[category ?? FALLBACK_CATEGORY];
}

/** Does this key belong to this category's pool? The predicate the category change turns on. */
export function coverKeyBelongsTo(key: string | null, category: PlanCategoryKey | null): boolean {
  if (key === null) return false;
  const pool = COVER_POOLS[category ?? FALLBACK_CATEGORY];
  return pool.includes(key);
}

/**
 * The cover a plan of this category gets, chosen deterministically from its pool.
 *
 * `seed` is the plan's name. An empty one still resolves — `fold('')` is a number like any other —
 * so a plan named later simply keeps the cover it was given, which is the behaviour a trainer
 * expects from an image that is already on screen.
 */
export function coverKeyForCategory(category: PlanCategoryKey | null, seed: string): string {
  const pool = COVER_POOLS[category ?? FALLBACK_CATEGORY];
  // The pools are literals in this file, so `pool[i]` is present for every `i` the modulo yields;
  // the fallback exists for `noUncheckedIndexedAccess` and names the case rather than asserting.
  return pool[fold(seed) % pool.length] ?? COVER_POOLS[FALLBACK_CATEGORY][0] ?? 'hybrid-02';
}

/**
 * The cover a plan should be RE-assigned when its category changes, or `null` when it already has
 * one that belongs.
 *
 * `null` means "leave it alone", which is what keeps a trainer's deliberate pick from being
 * overwritten every time the category radio is touched and put back.
 */
export function reassignCoverForCategory(
  currentKey: string | null,
  category: PlanCategoryKey | null,
  seed: string,
): string | null {
  return coverKeyBelongsTo(currentKey, category) ? null : coverKeyForCategory(category, seed);
}

/**
 * Which cover a plan is ACTUALLY showing. **Never null** — see the rules in the header.
 *
 * A key the manifest does not carry is treated as absent rather than trusted: the backend stores
 * whatever it is sent, so a plan authored by an older client, by the phone, or by a hand-rolled
 * request can arrive with a key that resolves to a 404. Falling back to the category's pool draws a
 * photograph that is at least about the right kind of training, which is the whole job of a cover.
 *
 * ── `seed` is the PLAN'S NAME, and it is a parameter because the two seeds have to agree ──────
 * The fallback used to fold `coverImageKey ?? ''`, which contradicted this module's own header —
 * "the seed is the plan's own NAME" — and, more usefully, contradicted the OTHER place a cover is
 * chosen. `reassignCoverForCategory` seeds from the name, so on a plan stored with no key at all
 * the two disagreed: the builder showed the `''`-seeded strength cover, switching to Cardio wrote
 * the NAME-seeded cardio one, and switching back wrote a name-seeded strength cover that was not
 * the photograph the trainer started from. One seed for both, and a category round trip comes home.
 *
 * Exported because a PICKER has to mark the option that is on screen, and it must not answer that
 * question a second way: a grid computing "selected" as `key === draft.coverImageKey` leaves every
 * thumbnail unmarked on a plan carrying no key at all, while the trigger beside it shows one of
 * them. The trainer then sees a cover and a picker that disagrees about which cover it is.
 */
export function resolvedCoverKey(
  coverImageKey: string | null | undefined,
  category: PlanCategoryKey | null,
  seed: string,
): string {
  return coverImageKey != null && ALL_COVER_KEYS.includes(coverImageKey)
    ? coverImageKey
    : coverKeyForCategory(category, seed);
}

/**
 * The URL to draw, for whichever cover `resolvedCoverKey` answers with. Never null.
 *
 * `seed` is the plan's name — the same one `reassignCoverForCategory` folds. A caller with no plan
 * behind the image (the library's empty-state hero) passes an empty string, which is a seed like any
 * other and lands on the neutral pool's single cover regardless.
 */
export function planCoverUrl(
  coverImageKey: string | null | undefined,
  category: PlanCategoryKey | null,
  seed: string,
  variant: PlanCoverVariant,
): string {
  return coverSource(resolvedCoverKey(coverImageKey, category, seed), variant);
}

/**
 * The picture a plan is DRAWN with: the server's `coverImageUrl` when there is one — the backend
 * resolved it from the plan's pool `coverId`, so a cover chosen on the phone shows here — and the
 * category's own deterministic bundled pick when there is not, so a plan never shows a blank frame
 * (owner rule D3c, "every plan has a cover").
 *
 * One rule, and until 23 September 2026 three copies of it: the library row, the builder's top bar
 * and the leftover-plan card each wrote the same `??` by hand.
 */
export function planDisplayCoverUrl(
  coverImageUrl: string | null | undefined,
  category: PlanCategoryKey | null,
  name: string,
  variant: PlanCoverVariant,
): string {
  return coverImageUrl ?? planCoverUrl(null, category, name, variant);
}

/** A plan's cover at both crops. Never null — see `planCoverPair`. */
export interface PlanCoverPair {
  readonly thumbUrl: string;
  readonly heroUrl: string;
}

/**
 * The SERVER's cover first, the bundled category fallback second — for both crops, in one place.
 *
 * `coverImageKey` was replaced on 14 August 2026 by a `coverId` into a server-side pool, so what a
 * row gets is the resolved URL rather than a key it could build a path from — and one URL serves
 * both crops, because the backend renders one asset. A plan with no cover still draws its
 * category's own deterministic pick, which is owner rule D3c's "every plan has a cover" unchanged.
 *
 * The plan's NAME is the fallback's seed, so a nameless plan still lands on a stable pick.
 *
 * Promoted out of `pages/plan-library` on 23 September 2026, when „Nowy program"'s template list
 * became its second consumer: a feature may not import a page.
 */
export function planCoverPair(
  coverImageUrl: string | null | undefined,
  categoryKey: PlanCategoryKey | null,
  name: string,
): PlanCoverPair {
  return {
    thumbUrl: planDisplayCoverUrl(coverImageUrl, categoryKey, name, 'thumb'),
    heroUrl: planDisplayCoverUrl(coverImageUrl, categoryKey, name, 'hero'),
  };
}
